import { now } from 'lodash';
import { db } from './db';
import type { DBPage } from './db';

export const fetchTagNames = () => {
  return db.tags.toCollection().uniqueKeys();
};

export function fetchTags(except: string[] = []) {
  return db.tags.toArray().then((tags) => {
    return tags.filter((tag) => !except.includes(tag.name));
  });
}
export const fetchTagsCount = () => db.tags.count();

// TODO: , sortOrder: PageSortOrder = 'recent'
export const fetchTagsForQuery = (queryTokens: string[]) => {
  if (queryTokens.length == 0) {
    return [db.tags.toArray(), () => {}];
  } else {
    return db.tags
      .filter((tag) => {
        const n = tag.name.toLowerCase(); // could materialize and index
        return queryTokens.every((token) => n.includes(token));
      })
      .toArray();
  }
};

export async function deleteTag(tag: string) {
  // Delete the tag record first.
  await db.tags.delete(tag);

  // Remove the tag from all pages in batches. .toCollection().modify() on a large
  // DB builds an enormous cursor chain and overflows the call stack; batched
  // bulkGet + bulkPut avoids that and keeps each write transaction short-lived.
  const allUrls = (await db.pages.toCollection().primaryKeys()) as string[];
  for (let i = 0; i < allUrls.length; i += 500) {
    const batchUrls = allUrls.slice(i, i + 500);
    await db.transaction('rw', [db.pages], async () => {
      const pages = await db.pages.bulkGet(batchUrls);
      const updates: DBPage[] = [];
      for (const page of pages) {
        if (!page) continue;
        if (
          page.humanTags.includes(tag) ||
          page.humanRejectedTags.includes(tag) ||
          page.autoTags.some((t) => t.name === tag)
        ) {
          updates.push({
            ...page,
            humanTags: page.humanTags.filter((x) => x !== tag),
            humanRejectedTags: page.humanRejectedTags.filter((x) => x !== tag),
            autoTags: page.autoTags.filter((t) => t.name !== tag),
          });
        }
      }
      if (updates.length > 0) await db.pages.bulkPut(updates);
    });
    await new Promise(r => setTimeout(r, 0));
  }
}

export async function fetchTagLastActive(tag: string) {
  const tagEntry = await db.tags.get(tag);
  return tagEntry?.last_active;
}

export async function renameTag(oldTag: string, newTag: string) {
  if (newTag == oldTag) {
    return;
  }

  // Update the tags table (outside page loop — no need to hold a page lock).
  await db.tags.put({ name: newTag, last_active: now() });
  await db.tags.delete(oldTag);

  // Rename the tag across all pages in batches. .toCollection().modify() on a large
  // DB builds an enormous cursor chain and overflows the call stack; batched
  // bulkGet + bulkPut avoids that and keeps each write transaction short-lived.
  const allUrls = (await db.pages.toCollection().primaryKeys()) as string[];
  for (let i = 0; i < allUrls.length; i += 500) {
    const batchUrls = allUrls.slice(i, i + 500);
    await db.transaction('rw', [db.pages], async () => {
      const pages = await db.pages.bulkGet(batchUrls);
      const updates: DBPage[] = [];
      for (const page of pages) {
        if (!page) continue;
        const hasOld =
          page.humanTags.includes(oldTag) ||
          page.humanRejectedTags.includes(oldTag) ||
          page.autoTags.some((t) => t.name === oldTag);
        if (!hasOld) continue;

        let humanTags = page.humanTags;
        if (humanTags.includes(oldTag)) {
          humanTags = humanTags.filter((x) => x !== oldTag);
          if (!humanTags.includes(newTag)) humanTags = [...humanTags, newTag];
        }

        let humanRejectedTags = page.humanRejectedTags;
        if (humanRejectedTags.includes(oldTag)) {
          humanRejectedTags = humanRejectedTags.filter((x) => x !== oldTag);
          if (!humanRejectedTags.includes(newTag)) humanRejectedTags = [...humanRejectedTags, newTag];
        }

        const seen = new Set<string>();
        const autoTags = page.autoTags
          .map((t) => (t.name === oldTag ? { ...t, name: newTag } : t))
          .filter((t) => {
            if (seen.has(t.name)) return false;
            seen.add(t.name);
            return true;
          });

        updates.push({ ...page, humanTags, humanRejectedTags, autoTags });
      }
      if (updates.length > 0) await db.pages.bulkPut(updates);
    });
    await new Promise(r => setTimeout(r, 0));
  }
}
